import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { GRAY_700, GRAY_900, GREEN_600 } from 'constants/styling/theme'
import { Trans, useTranslation } from 'react-i18next'

import { CatalogueProduct } from 'dataQueries/purchase.query'
import { CounterInput } from 'components/common/CounterInput'
import { Currency } from 'constants/misc'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import { ProductCategory } from 'models/product'
import { ProductKind } from 'constants/product'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { bigFromDecimal } from 'utils/price/decimals'
import { formatPrice } from 'utils/price'
import { getProductDetailsPage } from 'utils/localization/productDetails'
import { isEditingCategory } from 'utils/validators'
import { useEmbedConfig } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConfig.context'
import { useMemo } from 'react'

/** @interface Props for the ProductCardHeader component. */
interface Props {
  /** Product whose info is to be displayed */
  product: CatalogueProduct
  /** Product category */
  category: ProductCategory
  /** Currency for price formatting */
  currency: Currency
  /** Whether the header should appear disabled */
  disabled: boolean
  /** Type key of product (for non-editing products) */
  typeKey?: string
  /** Whether to show the price of the product or not. */
  displayPrice?: boolean
  /** Quantity of the selected product. */
  quantity?: number
  /** Is the product optional */
  optional?: boolean
}

/** 
 * @component Shows styled header with product name, description and price. Made to be used in a card.
 * @example
 * <EditingProductCardHeader
 *  product={productObject}
 *  category={productCategory}
 *  disabled={false}
 * />
*/
export const ProductCardHeader: React.FC<Props> = ({
  product,
  category,
  typeKey,
  currency,
  disabled = false,
  displayPrice = true,
  quantity,
  optional = false,
}) => {
  const { t, i18n } = useTranslation(['order', 'product', 'product_description'])
  const { isEmbed } = useEmbedConfig()

  // Bundle discount: the original price is struck only when it genuinely exceeds the fee. Compared on Big
  // values, never on formatted strings — a channel mark-up (original < fee) must not render as a discount.
  const showDiscountedPrice = useMemo(() => {
    if (product.kind !== ProductKind.BUNDLE) return false
    if (!product.originalPrice) return false // edge case - not strictly mandatory to display bundle products with discounts (for special products only)
    return bigFromDecimal(product.originalPrice.value).gt(bigFromDecimal(product.feePrice.value))
  }, [product.feePrice, product.kind, product.originalPrice])

  // Embed-only OneSystem/channel discount: the base (public) price is struck through
  // whenever it exceeds the effective price. Comparison is on Big values, never on
  // formatted-price strings. Covered quota packs (feePrice == 0 && originalPrice > 0)
  // additionally render the effective price in green. Non-embed rendering is untouched.
  const embedChannelDiscount = useMemo(() => {
    if (!isEmbed || !product.originalPrice) return false
    return bigFromDecimal(product.originalPrice.value).gt(bigFromDecimal(product.feePrice.value))
  }, [isEmbed, product.originalPrice, product.feePrice])

  const showStruckOriginal = showDiscountedPrice || embedChannelDiscount
  const isCovered = embedChannelDiscount && bigFromDecimal(product.feePrice.value).eq(0)

  // In the embed the single headline price is what the cart charges (feePrice). The base "original" is only
  // a strike-through reference for a discount — a channel priced ABOVE the base book must not show the cheaper
  // base price as the product's price while the cart charges more (ISSUE-3241). Non-embed rendering is untouched.
  const headlinePrice = isEmbed && !showStruckOriginal ? product.feePrice : (product.originalPrice ?? product.feePrice)

  const productDetailsPage = getProductDetailsPage(
    i18n.language,
    product.kind,
    product.type,
    product.documentSubtype,
    product.id
  )

  const getDescription = (key: string) => i18n.exists(key) ? <Trans t={t} i18nKey={key} /> : null

  const getComparedDescription = (keys: string[]) => {
    const descriptionKey = keys.find(key => i18n.exists(key))
    return descriptionKey && <Trans t={t} i18nKey={descriptionKey} />
  }

  return (
    <Stack
      gap={1}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ opacity: disabled ? 0.5 : 1 }}
    >

      <Stack>

        <Stack gap={2} direction="row" alignItems="center">

          {/** PRODUCT QUANTITY */}
          {quantity && quantity > 0 && (
            <CounterInput
              value={quantity}
              hideControls
              disabled
            />
          )}

          <Stack>

            <Stack flexDirection="row" alignItems="center" gap=".5rem">
              {/** PRODUCT NAME */}
              <Typography variant='text-md' fontWeight={600} color={GRAY_900}>
                {t(`product:p_${product.id}`)}{optional ? ` ${t('order:product_is_optional')}` : ''}
              </Typography>

              {productDetailsPage &&
                <a
                  href={productDetailsPage}
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  onClick={(e) => {
                    logAnalyticsEvent(AnalyticsEvent.PROFOLIO_OPENED, { ProductKind: product.kind })
                    e.stopPropagation()
                  }}
                >
                  <HelpOutlineRoundedIcon sx={{ color: GRAY_900, paddingTop: '.3rem' }} />
                </a>
              }
            </Stack>

            {/** PRODUCT DESCRIPTION (Editing category) */}
            {isEditingCategory(category) &&
              <Typography variant='text-sm'>
                {getComparedDescription([
                  `product_description:pd_${product.id}`,
                  `product_kind_description:${product.kind}`
                ])}
                {getDescription(`step_product.category_${category}_type_${product.kind}`)}
              </Typography>
            }

          </Stack>

        </Stack>

        {/** PRODUCT DESCRIPTION (Shooting category) */}
        {!isEditingCategory(category) &&
          <Typography variant='text-sm'>
            {getComparedDescription([
              `product_description:pd_${product.id}`,
              `product_kind_description:${product.kind}`
            ])}
            {!!typeKey && getDescription(`product_type_description:${typeKey}`)}
            {!!typeKey && getDescription(`step_product.category_${category}_type_${typeKey}`)}
          </Typography>
        }
      </Stack>

      {/** PRODUCT PRICE */}
      {displayPrice &&
        <Stack justifyContent="center" textAlign="end">

          {/** EFFECTIVE PRICE (bundle discount or embed channel discount) — green when quota-covered */}
          {showStruckOriginal &&
            <Typography variant='text-sm' fontWeight={600} color={isCovered ? GREEN_600 : GRAY_900}>
              {formatPrice(product.feePrice, currency)}
            </Typography>
          }

          {/** ORIGINAL (struck) OR EFFECTIVE PRICE */}
          <Typography
            variant='text-sm'
            fontWeight={600}
            color={showStruckOriginal ? GRAY_700 : GRAY_900}
            sx={{ textDecoration: showStruckOriginal ? 'line-through' : 'inherit' }}
          >
            {formatPrice(headlinePrice, currency)}
          </Typography>

        </Stack>
      }

    </Stack>
  )
}
